import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Textarea } from '@components';
import { ApiClient, parseSSEStream } from '@utils/apiClient';
import { QuestionsOverlay, PromptArchitectQuestion } from './QuestionsOverlay';
import { SimpleMarkdown } from './SimpleMarkdown';

export type PromptArchitectMode = 'create' | 'optimize';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PromptArchitectContext {
  agent_name?: string;
  current_prompt?: string;
  has_knowledge_base?: boolean;
  has_calendars?: boolean;
  has_call_routes?: boolean;
  has_web_search?: boolean;
}

interface PromptArchitectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (prompt: string) => void;
  context: PromptArchitectContext;
  /** 'optimize' when the agent already has a system prompt, else 'create'. */
  initialMode: PromptArchitectMode;
}

/** Extract the final agent.prompt from a ```markdown ... ``` code block. */
const extractFinalPrompt = (text: string): string | null => {
  const match = text.match(/```(?:markdown|md)?\s*\n([\s\S]*?)```/);
  if (match && match[1].trim()) {
    return match[1].trim();
  }
  return null;
};

const QUESTIONS_BLOCK_RE = /```questions\s*\n([\s\S]*?)```/;

/** Parse a ```questions ... ``` JSON block into structured questions. */
const extractQuestions = (text: string): PromptArchitectQuestion[] | null => {
  const match = text.match(QUESTIONS_BLOCK_RE);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1].trim());
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const valid = parsed.filter(
      (q): q is PromptArchitectQuestion =>
        q &&
        typeof q.id === 'string' &&
        typeof q.label === 'string' &&
        (q.type === 'text' || q.type === 'single' || q.type === 'multi')
    );
    return valid.length ? valid : null;
  } catch {
    return null;
  }
};

/** Hide the raw questions block from the visible chat bubble. */
const stripQuestionsBlock = (text: string): string =>
  text.replace(QUESTIONS_BLOCK_RE, '').replace(/\n{3,}/g, '\n\n').trim();

export const PromptArchitectModal: React.FC<PromptArchitectModalProps> = ({
  isOpen,
  onClose,
  onApply,
  context,
  initialMode,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PromptArchitectMode>(initialMode);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [activeQuestions, setActiveQuestions] = useState<PromptArchitectQuestion[] | null>(null);
  const [answeredKeys, setAnsweredKeys] = useState<Set<string>>(new Set());
  // Only surface the wizard overlay when the user explicitly chose a guided flow
  // (interview / full analysis). Otherwise questions stay inline in the chat.
  const [guidedMode, setGuidedMode] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const streamReply = useCallback(
    async (history: ChatMessage[], activeMode: PromptArchitectMode) => {
      setIsStreaming(true);
      setError(null);
      // Add an empty assistant message we fill as tokens arrive.
      setMessages([...history, { role: 'assistant', content: '' }]);

      try {
        const response = await ApiClient.postStream('/agents/prompt-architect/chat/', {
          messages: history,
          mode: activeMode,
          context,
        });

        if (!response.ok) {
          throw new Error('request_failed');
        }

        let assistantText = '';
        for await (const event of parseSSEStream(response)) {
          if (event.type === 'delta' && event.content) {
            assistantText += event.content;
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: assistantText };
              return next;
            });
          } else if (event.type === 'error') {
            throw new Error('stream_error');
          }
        }
      } catch {
        setError(t('agents.promptArchitect.error'));
        // Drop the empty/partial assistant bubble on hard failure.
        setMessages(prev => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && !next[next.length - 1].content) {
            next.pop();
          }
          return next;
        });
      } finally {
        setIsStreaming(false);
      }
    },
    [context, t]
  );

  // Show a static greeting when the modal opens — no LLM call until the user acts.
  // This avoids an automatic (token-costing) analysis/interview turn on every open.
  useEffect(() => {
    if (isOpen && !started) {
      setStarted(true);
      setMode(initialMode);
      const greeting =
        initialMode === 'optimize'
          ? t('agents.promptArchitect.greetingOptimize')
          : t('agents.promptArchitect.greetingCreate');
      setMessages([{ role: 'assistant', content: greeting }]);
    }
    if (!isOpen && started) {
      // Reset for next open.
      setStarted(false);
      setMessages([]);
      setInput('');
      setError(null);
      setActiveQuestions(null);
      setAnsweredKeys(new Set());
      setGuidedMode(false);
    }
  }, [isOpen, started, initialMode, t]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    const history: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setInput('');
    streamReply(history, mode);
  };

  // Quick-start actions: user explicitly opts into an interview or full analysis.
  const handleQuickStart = (intent: 'interview' | 'analyze' | 'draft') => {
    if (isStreaming) return;
    // Interview and full analysis are guided flows → show the questions overlay.
    setGuidedMode(intent === 'interview' || intent === 'analyze');
    const key =
      intent === 'interview'
        ? 'agents.promptArchitect.quickInterviewMsg'
        : intent === 'analyze'
          ? 'agents.promptArchitect.quickAnalyzeMsg'
          : 'agents.promptArchitect.quickDraftMsg';
    const history: ChatMessage[] = [...messages, { role: 'user', content: t(key) }];
    streamReply(history, mode);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // The latest assistant message may contain the finished prompt.
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
  const finalPrompt = lastAssistant ? extractFinalPrompt(lastAssistant.content) : null;

  // Auto-open the questions overlay only in a guided flow (interview / analysis).
  // In free chat, questions stay inline so the user can just keep typing.
  useEffect(() => {
    if (!guidedMode || isStreaming || !lastAssistant) return;
    const questions = extractQuestions(lastAssistant.content);
    if (!questions) return;
    const key = questions.map(q => q.id).join('|');
    if (answeredKeys.has(key)) return;
    setActiveQuestions(questions);
  }, [guidedMode, isStreaming, lastAssistant, answeredKeys]);

  const handleQuestionsSubmit = (answerText: string) => {
    if (activeQuestions) {
      const key = activeQuestions.map(q => q.id).join('|');
      setAnsweredKeys(prev => new Set(prev).add(key));
    }
    setActiveQuestions(null);
    const history: ChatMessage[] = [...messages, { role: 'user', content: answerText }];
    streamReply(history, mode);
  };

  const handleQuestionsSkip = () => {
    if (activeQuestions) {
      const key = activeQuestions.map(q => q.id).join('|');
      setAnsweredKeys(prev => new Set(prev).add(key));
    }
    setActiveQuestions(null);
  };

  const renderBubble = (msg: ChatMessage): React.ReactNode => {
    if (msg.role !== 'assistant') {
      return <span className="whitespace-pre-wrap">{msg.content}</span>;
    }
    const hasQuestions = QUESTIONS_BLOCK_RE.test(msg.content);
    const visible = hasQuestions ? stripQuestionsBlock(msg.content) : msg.content;
    // In free chat, render the questions inline as a readable list so the user
    // can answer directly in the composer without a wizard overlay.
    const inlineQuestions =
      hasQuestions && !guidedMode ? extractQuestions(msg.content) : null;

    if (visible || inlineQuestions) {
      return (
        <>
          {visible && <SimpleMarkdown text={visible} />}
          {inlineQuestions && (
            <ul className="mt-2 ml-5 list-disc space-y-0.5">
              {inlineQuestions.map(q => (
                <li key={q.id}>{q.label}</li>
              ))}
            </ul>
          )}
        </>
      );
    }
    if (hasQuestions) return t('agents.promptArchitect.questionsInline');
    return <span className="material-icons text-sm animate-spin">refresh</span>;
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('agents.promptArchitect.title')}
      size="xl"
    >
      <div className="flex flex-col h-[70vh]">
        {/* Chat history */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto space-y-4 pr-1"
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
            >
              <div
                className={
                  msg.role === 'user'
                    ? 'max-w-[85%] rounded-lg bg-primary-500 px-4 py-2 text-sm text-white whitespace-pre-wrap'
                    : 'max-w-[85%] rounded-lg bg-gray-100 dark:bg-gray-700 px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap'
                }
              >
                {renderBubble(msg)}
              </div>
            </div>
          ))}
        </div>

        {/* Optional quick-start actions (only before the first real turn). */}
        {messages.length <= 1 && !isStreaming && (
          <div className="mt-3 flex flex-wrap gap-2">
            {mode === 'optimize' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickStart('analyze')}
              >
                {t('agents.promptArchitect.quickAnalyze')}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStart('draft')}
                >
                  {t('agents.promptArchitect.quickDraft')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStart('interview')}
                >
                  {t('agents.promptArchitect.quickInterview')}
                </Button>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="mt-2 text-sm text-red-500">{error}</div>
        )}

        {/* Apply finished prompt */}
        {finalPrompt && !isStreaming && (
          <div className="mt-3 rounded-lg border border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 p-3">
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-300">
              {t('agents.promptArchitect.readyHint')}
            </p>
            <Button
              variant="gradient-multi"
              className="w-full"
              onClick={() => {
                onApply(finalPrompt);
                onClose();
              }}
            >
              <span className="material-icons text-sm mr-1">check</span>
              {t('agents.promptArchitect.apply')}
            </Button>
          </div>
        )}

        {/* Composer */}
        <div className="mt-3 flex items-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="flex-1">
            <Textarea
              fullWidth
              autoResize
              minRows={3}
              maxRows={10}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('agents.promptArchitect.inputPlaceholder')}
              disabled={isStreaming}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={isStreaming || !input.trim()}
          >
            {isStreaming ? (
              <span className="material-icons text-sm animate-spin">refresh</span>
            ) : (
              <span className="material-icons text-sm">send</span>
            )}
          </Button>
        </div>
      </div>
    </Modal>

    {activeQuestions && (
      <QuestionsOverlay
        isOpen={Boolean(activeQuestions)}
        questions={activeQuestions}
        onClose={handleQuestionsSkip}
        onSubmit={handleQuestionsSubmit}
      />
    )}
    </>
  );
};

export default PromptArchitectModal;
